<script setup lang="ts">
import KgrConfirmDialog from '../../components/common/modal/ConfirmDialog.vue';
import KgrDatePicker from '../../components/common/form/picker/DatePicker.vue';
import KgrIcon from '@legacy/components/common/icon/Icon.vue';
import KgrLoading from '../../components/common/Loading.vue';
import type { MembershipChangeRequest } from './membershipChangeRequestTypes';
import { ref } from 'vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

defineProps<{
    request: MembershipChangeRequest;
    memberId: number;
    processing: boolean;
}>();

const emit = defineEmits<{
    process: [request: MembershipChangeRequest, date: string];
    processDeletion: [request: MembershipChangeRequest];
    reject: [request: MembershipChangeRequest];
}>();

const date = ref<string>();
const deleteConfirmDialog = ref<InstanceType<typeof KgrConfirmDialog> | null>(null);
const rejectConfirmDialog = ref<InstanceType<typeof KgrConfirmDialog> | null>(null);

function isOpen(request: MembershipChangeRequest): boolean {
    return !request.processedAt && !request.rejectedAt;
}

function isContractChange(request: MembershipChangeRequest): boolean {
    return request.type === 'contract_change';
}

function isDepartmentJoin(request: MembershipChangeRequest): boolean {
    return request.type === 'department_join';
}

function targetLabelKey(request: MembershipChangeRequest): string {
    if (isContractChange(request)) {
        return 'Mitgliedschaft';
    }

    return request.type === 'team_termination' ? 'Team' : 'Abteilung';
}

function contractTypeLabelKey(request: MembershipChangeRequest): string {
    switch (request.contractType) {
        case 'regular':
            return 'Aktive Mitgliedschaft';
        case 'passive':
            return 'Passive Mitgliedschaft';
        default:
            return 'Gastmitgliedschaft';
    }
}

function parseCurrentStart(request: MembershipChangeRequest): Date | null {
    if (request.currentStart === null) {
        return null;
    }

    const [day, month, year] = request.currentStart.split('.').map(Number);
    return new Date(year, month - 1, day);
}

function isFutureStart(request: MembershipChangeRequest): boolean {
    const start = parseCurrentStart(request);
    if (start === null) {
        return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return start > today;
}

function minProcessDate(request: MembershipChangeRequest): Date | undefined {
    // For terminations and active/passive switches the process date is an end date and must not
    // precede the current start; joins pick a start date and are not constrained by it.
    if (isDepartmentJoin(request)) {
        return undefined;
    }

    return parseCurrentStart(request) ?? undefined;
}

function startLabel(request: MembershipChangeRequest): string {
    return isFutureStart(request) ? 'ab' : 'seit';
}

function canDeleteMembership(request: MembershipChangeRequest): boolean {
    if (isContractChange(request) && request.action !== 'terminate') {
        return false;
    }

    return isOpen(request) && isFutureStart(request);
}

async function requestDeletion(request: MembershipChangeRequest) {
    const confirmed = await deleteConfirmDialog.value?.open();
    if (confirmed) {
        emit('processDeletion', request);
    }
}

async function requestReject(request: MembershipChangeRequest) {
    const confirmed = await rejectConfirmDialog.value?.open();
    if (confirmed) {
        emit('reject', request);
    }
}

function dateLabelKey(request: MembershipChangeRequest): string {
    if (isDepartmentJoin(request)) {
        return 'Beginn der Zugehörigkeit';
    }

    if (isContractChange(request) && request.action !== 'terminate') {
        return 'Wirksam zum';
    }

    return 'Enddatum';
}

function processDateLabelKey(request: MembershipChangeRequest): string {
    if (isDepartmentJoin(request)) {
        return 'Beginn der Zugehörigkeit';
    }

    if (isContractChange(request) && request.action !== 'terminate') {
        return 'Wechsel zu Datum';
    }

    return 'Mitgliedschaft beenden zu Datum';
}

function process(request: MembershipChangeRequest) {
    if (!date.value) {
        return;
    }

    emit('process', request, date.value);
}
</script>

<template>
    <div>
        <div class="kgr-row">
            <a
                class="kgr-button kgr-button--link"
                :href="`/backend/clubs/members/${memberId}/show`"
            >
                <kgr-icon
                    icon-class="user"
                    class="kgr-button__icon kgr-button__icon--left kgr-icon"
                />
                {{ $t('Kurzprofil anzeigen') }}
            </a>
        </div>

        <dl class="kgr-definitionList kgr-definitionList--horizontal kgr-definitionList--175 kgr-row">
            <dt class="kgr-definitionList__term">
                {{ $t('Änderung beantragt am') }}
            </dt>
            <dd class="kgr-definitionList__definition">
                {{ request.requestedAt }}
            </dd>
            <template v-if="request.processedAt">
                <dt class="kgr-definitionList__term">
                    {{ $t('Änderung bearbeitet am') }}
                </dt>
                <dd class="kgr-definitionList__definition">
                    {{ request.processedAt }}
                </dd>
            </template>
            <template v-if="request.rejectedAt">
                <dt class="kgr-definitionList__term">
                    {{ $t('Änderung abgelehnt am') }}
                </dt>
                <dd class="kgr-definitionList__definition">
                    {{ request.rejectedAt }}
                </dd>
            </template>
            <template v-if="request.processedBy">
                <dt class="kgr-definitionList__term">
                    {{ $t('Bearbeitet von') }}
                </dt>
                <dd class="kgr-definitionList__definition">
                    {{ request.processedBy }}
                </dd>
            </template>
            <dt class="kgr-definitionList__term">
                {{ $t(targetLabelKey(request)) }}
            </dt>
            <dd class="kgr-definitionList__definition">
                <template v-if="isContractChange(request)">
                    <template v-if="request.contractType">
                        {{ $t(contractTypeLabelKey(request)) }}
                    </template>
                    <template v-else>
                        –
                    </template>
                </template>
                <template v-else>
                    {{ request.targetName ?? '–' }}
                </template>
            </dd>
            <template v-if="request.membershipNumber">
                <dt class="kgr-definitionList__term">
                    {{ $t('Mitgliedsnummer') }}
                </dt>
                <dd class="kgr-definitionList__definition">
                    {{ request.membershipNumber }}
                </dd>
            </template>
            <dt class="kgr-definitionList__term">
                {{ $t('Laufzeit') }}
            </dt>
            <dd class="kgr-definitionList__definition">
                <template v-if="request.currentStart === null">
                    –
                </template>
                <template v-else-if="request.currentEnd">
                    {{ request.currentStart }} – {{ request.currentEnd }}
                </template>
                <template v-else>
                    {{ startLabel(request) }} {{ request.currentStart }}
                </template>
            </dd>
            <template v-if="request.scheduledDate">
                <dt class="kgr-definitionList__term">
                    {{ $t(dateLabelKey(request)) }}
                </dt>
                <dd class="kgr-definitionList__definition">
                    {{ request.scheduledDate }}
                </dd>
            </template>
            <dd
                v-if="request.membershipDeleted"
                class="kgr-definitionList__definition"
            >
                {{ $t('Die Zugehörigkeit wurde komplett gelöscht, da sie noch nicht begonnen hatte.') }}
            </dd>
        </dl>

        <div v-if="isOpen(request)">
            <div class="kgr-postfix kgr-postfix--bottom kgr-postfix--gutters kgr-row kgr-row--noneIfLastChild">
                <div class="kgr-postfix__fixed">
                    <label class="kgr-inputGroup">
                        <span class="kgr-inputGroup__label">{{ $t(processDateLabelKey(request)) }}</span>
                        <kgr-date-picker
                            v-model="date"
                            :min-date="minProcessDate(request)"
                            :disabled="processing"
                        />
                    </label>
                </div>
                <div class="kgr-postfix__fluid">
                    <kgr-loading
                        v-if="processing"
                    />
                    <template v-else>
                        <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--between">
                            <div class="kgr-postfix__fixed">
                                <button
                                    class="kgr-button kgr-button--accent"
                                    :disabled="!date"
                                    @click="process(request)"
                                >
                                    <kgr-icon
                                        icon-class="check"
                                        class="kgr-button__icon kgr-button__icon--left"
                                    />
                                    {{ $t('Änderung übernehmen') }}
                                </button>
                            </div>
                            <div class="kgr-postfix__fixed">
                                <button
                                    class="kgr-button kgr-button--danger"
                                    @click="requestReject(request)"
                                >
                                    <kgr-icon
                                        icon-class="block"
                                        class="kgr-button__icon kgr-button__icon--left"
                                    />
                                    {{ $t('Änderung ablehnen') }}
                                </button>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <div
                v-if="canDeleteMembership(request) && !processing"
                class="kgr-postfix kgr-postfix--bottom kgr-postfix--gutters kgr-row kgr-row--noneIfLastChild"
            >
                <div class="kgr-inputGroup">
                    <div class="kgr-inputGroup__label">
                        {{ $t('Nicht begonnene Mitgliedschaft löschen') }}
                    </div>
                    <button
                        class="kgr-button kgr-button--danger"
                        @click="requestDeletion(request)"
                    >
                        {{ $t('Mitgliedschaft löschen') }}
                    </button>
                </div>
            </div>
        </div>

        <kgr-confirm-dialog
            ref="deleteConfirmDialog"
            :headline="$t('Mitgliedschaft löschen')"
        >
            {{ $t('Die Zugehörigkeit hat noch nicht begonnen und wird ersatzlos gelöscht. Fortfahren?') }}
        </kgr-confirm-dialog>

        <kgr-confirm-dialog
            ref="rejectConfirmDialog"
            :headline="$t('Änderungswunsch ablehnen')"
        >
            {{
                $t(
                    'Der Änderungswunsch wird abgelehnt. Es wird kein Dokument erstellt und keine E-Mail versendet. Fortfahren?',
                )
            }}
        </kgr-confirm-dialog>
    </div>
</template>

<style scoped lang="scss" rel="stylesheet/scss"></style>
